import {PublishCommand} from './types.js';
import {IOGateway} from '../../../gateway';
import {RestPublisherConfig, Fetch} from '../../../types/metrics/publisher/rest';

async function discardResponseBody(response: Response) {
    if (!response.bodyUsed) {
        await response.blob();
    }
}

const SUCCESS_STATUSES = new Set<number>([200/*OK*/, 201/*CREATED*/, 202/*ACCEPTED*/]);

function basicAuthCredentials({user, password}: { user: string; password: string }) {
    const data = `${user}:${password}`;
    if (typeof process !== 'undefined') {
        return Buffer.from(data).toString('base64');
    } else {
        return globalThis.btoa(data);
    }
}

export const name = 'rest';

async function login(url: URL, {logger, fetchFn, headers, timeout}: HandleContext) {

    if (logger.enabledFor('debug')) {
        logger.debug(`sending initial GET to do auth dance. url: ${url}`);
    }
    try {
        const response = await fetchFn(url, initRequest(headers), timeout);
        if (response.ok) {
            logger.info(`initial GET request completed successfully.`);
            if (logger.enabledFor('debug')) {
                const setCookie = response.headers.getSetCookie();
                if (setCookie.length > 0) {
                    if (logger.enabledFor('debug')) {
                        logger.debug(`will set following cookies: ${setCookie}`);
                    }
                }
            }
        } else {
            logger.warn(`initial GET request did not completed successfully. Status: ${response.status}`);
        }
    } catch (e) {
        logger.warn(`error executing initial GET request`, e);
        throw e;
    }
}

async function handleStart(auth: RestPublisherConfig['authentication'], context: HandleContext) {
    if (!isBasicAuthorization(auth) && auth !== false) {
        const url = new URL(context.endpoint);
        if (auth?.path) {
            url.pathname = auth.path;
        }
        await login(url, context)
    }
    context.logger.info('rest publisher started');
}

function handleStop({logger}: HandleContext) {
    logger.info('rest publisher stopped');
}

function initRequest(headers: Headers, m?: IOGateway.Metrics.Update): RequestInit {
    return m ?
        {
            method: 'POST',
            redirect: 'manual',
            credentials: 'include',
            headers,
            body: JSON.stringify(m)
        }
        :
        {
            method: 'GET',
            redirect: 'follow',
            credentials: 'include',
            headers
        }
}

// a helper type to track state changes
// good -> bad
// bad -> good
type PublisherState = {
    // indicates if previous state was healthy
    prev: boolean
    // indicates
    now: boolean
};

async function handleUpdate(m: IOGateway.Metrics.Update, state: PublisherState, {
    endpoint,
    headers,
    timeout,
    fetchFn,
    logger
}: HandleContext) {
    const request = initRequest(headers, m);

    try {
        let response = await fetchFn(endpoint, request, timeout);
        if (response.redirected) {
            logger.info(`Redirect detected will do GET to allow auth dance and re-POST`);
            await discardResponseBody(response);
            const getResponse = await fetchFn(endpoint, initRequest(headers), timeout);
            await discardResponseBody(getResponse);
            if (getResponse.ok) {
                response = await fetchFn(endpoint, request, timeout);
            } else {
                logger.error(`GET failed with status: ${getResponse.status} headers: ${JSON.stringify(getResponse.headers)}`);
            }
        }
        await discardResponseBody(response);
        state.prev = state.now;
        state.now = SUCCESS_STATUSES.has(response.status);
        if (state.prev && !state.now) {
            logger.error(`Unable to publish metrics. status ${response.status} headers: ${JSON.stringify(response.headers)} msg: ${JSON.stringify(m)}`);
        } else if (state.now && !state.prev) {
            logger.info(`Publishing metrics.`);
        }
    } catch (e) {
        const logError = state.now;
        state.prev = state.now;
        state.now = false;
        if (logError) {
            logger.error(`Unable to publish metrics. msg ${JSON.stringify(m)}`, e);
        }
    }
}

type HandleContext = {
    endpoint: string | URL
    headers: Headers
    timeout: number
    fetchFn: Fetch
    logger: IOGateway.Logging.Logger
}

function isBasicAuthorization(auth?: RestPublisherConfig['authentication']): auth is {user: string, password: string} {
    return auth !== undefined && auth && (auth['user'] !== undefined);
}

export const fetchWithTimeout = () => {
    return async (input: string | URL , request: Omit<RequestInit, 'signal'>, timeout: number): Promise<Response> => {
        const controller = new AbortController();
        let timedOut = false;
        const id = setTimeout(() => {
            timedOut = true;
            controller.abort(`Request to ${input} timed out after ${timeout} ms`);
        }, timeout);
        try {
            return await fetch(input, {...request, signal: controller.signal})
                .catch((err) => {
                    if (timedOut) {
                        throw new Error(`Request ${JSON.stringify(input)} timed out`);
                    }
                    throw err;
                });
        } finally {
            if (id !== undefined) {
                clearTimeout(id);
            }
        }
    };
}
export function create(cfg: RestPublisherConfig, logger: IOGateway.Logging.Logger) {
    const endpoint = cfg.endpoint;
    const timeout = cfg.timeout ?? 1000;
    const headers = new Headers(cfg.headers);
    if (!headers.has('Content-Type')) {
        headers.set('Content-Type', 'application/json');
    }
    if (!headers.has('User-Agent')) {
        headers.set('User-Agent', cfg['user-agent'] ?? 'metrics-rest/0.2.x');
    }
    if (isBasicAuthorization(cfg.authentication) && !headers.has('Authorization')) {
        headers.set('Authorization', `Basic ${basicAuthCredentials(cfg.authentication)}`);
    }

    const fetchFn: Fetch = cfg.fetch ?? fetchWithTimeout();
    const state = {prev: false, now: true};
    const context: HandleContext = {endpoint, headers, timeout, fetchFn, logger};
    return async (m: PublishCommand) => {
        if (logger.enabledFor('debug')) {
            logger.debug(`handling publish request: ${JSON.stringify(m)}`);
        }
        switch (m) {
            case 'start': {
                await handleStart(cfg.authentication, context);
                break;
            }
            case 'stop': {
                handleStop(context);
                break;
            }
            default: {
                await handleUpdate(m, state, context);
                break;
            }
        }
    }
}
